import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from './use-toast';
import { useAuth } from '@/context/AuthContext';

interface ApiError {
  message: string;
  status?: number;
}

interface User {
  id: number;
  clerk_user_id: string;
  email: string;
  name: string | null;
  created_at: string;
  updated_at: string;
  profile_picture_url: string | null;
  last_login_at: string | null;
}

interface Idea {
  id: number;
  user_id: number;
  title: string;
  content: string | null;
  notebook_id: number | null;
  status: string;
  created_at: string;
  updated_at: string;
  is_favorite: boolean;
}

interface Notebook {
  id: number;
  user_id: number;
  name: string;
  description: string | null;
  color: string;
  created_at: string;
  updated_at: string;
}

const API_BASE_URL = '/api';

async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit & { skipAuth?: boolean }
): Promise<Response> {
  const response = await fetch(input, init);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw {
      message: errorData.message || 'Request failed',
      status: response.status,
    };
  }

  return response;
}

export function useUserProfile() {
  const { user } = useAuth();

  return useQuery<User, ApiError>({
    queryKey: ['user', user?.id],
    queryFn: async () => {
      if (!user) throw new Error('No user authenticated');

      const response = await fetchWithAuth(
        `${API_BASE_URL}/users/me?clerk_user_id=${user.id}`
      );
      return response.json();
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation<User, ApiError, Omit<User, 'id' | 'created_at' | 'updated_at'>>({
    mutationFn: async (userData) => {
      const response = await fetchWithAuth(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['user', data.user.id], data.user);
      toast({
        title: 'Account created',
        description: 'Your account has been successfully created',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create account',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation<User, ApiError, Partial<Omit<User, 'id' | 'clerk_user_id' | 'email'>>>({
    mutationFn: async (updates) => {
      if (!user?.id) throw new Error('No user authenticated');

      const response = await fetchWithAuth(`${API_BASE_URL}/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['user', data.user.id], data.user);
      toast({
        title: 'Profile updated',
        description: 'Your profile has been successfully updated',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update profile',
        variant: 'destructive',
      });
    },
  });
}

// Idea Hooks
export function useIdeas() {
  const { user } = useAuth();

  return useQuery<Idea[], ApiError>({
    queryKey: ['ideas', user?.id],
    queryFn: async () => {
      if (!user) throw new Error('No user authenticated');

      const response = await fetchWithAuth(
        `${API_BASE_URL}/ideas?user_id=${user.id}`
      );
      return response.json();
    },
    enabled: !!user,
  });
}

export function useCreateIdea() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation<Idea, ApiError, Omit<Idea, 'id' | 'user_id' | 'created_at' | 'updated_at'>>({
    mutationFn: async (ideaData) => {
      if (!user?.id) throw new Error('No user authenticated');

      const response = await fetchWithAuth(`${API_BASE_URL}/ideas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...ideaData, user_id: user.id }),
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ideas', user?.id]);
      toast({
        title: 'Idea created',
        description: 'Your idea has been successfully saved',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create idea',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateIdea() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation<Idea, ApiError, { id: number; updates: Partial<Omit<Idea, 'id' | 'user_id' | 'created_at'>> }>({
    mutationFn: async ({ id, updates }) => {
      const response = await fetchWithAuth(`${API_BASE_URL}/ideas/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ideas', user?.id]);
      toast({
        title: 'Idea updated',
        description: 'Your idea has been successfully updated',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update idea',
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteIdea() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation<{ success: boolean }, ApiError, number>({
    mutationFn: async (ideaId) => {
      const response = await fetchWithAuth(`${API_BASE_URL}/ideas/${ideaId}`, {
        method: 'DELETE',
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ideas', user?.id]);
      toast({
        title: 'Idea deleted',
        description: 'Your idea has been successfully deleted',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete idea',
        variant: 'destructive',
      });
    },
  });
}

// Notebook Hooks
export function useNotebooks() {
  const { user } = useAuth();

  return useQuery<Notebook[], ApiError>({
    queryKey: ['notebooks', user?.id],
    queryFn: async () => {
      if (!user) throw new Error('No user authenticated');

      const response = await fetchWithAuth(
        `${API_BASE_URL}/notebooks?user_id=${user.id}`
      );
      return response.json();
    },
    enabled: !!user,
  });
}

export function useDefaultNotebook() {
  const { user } = useAuth();

  return useQuery<Notebook, ApiError>({
    queryKey: ['default-notebook', user?.id],
    queryFn: async () => {
      if (!user) throw new Error('No user authenticated');

      const response = await fetchWithAuth(
        `${API_BASE_URL}/notebooks/default?user_id=${user.id}`
      );
      return response.json();
    },
    enabled: !!user,
  });
}

export function useCreateNotebook() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation<Notebook, ApiError, Omit<Notebook, 'id' | 'user_id' | 'created_at' | 'updated_at'>>({
    mutationFn: async (notebookData) => {
      if (!user?.id) throw new Error('No user authenticated');

      const response = await fetchWithAuth(`${API_BASE_URL}/notebooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...notebookData, user_id: user.id }),
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['notebooks', user?.id]);
      toast({
        title: 'Notebook created',
        description: 'Your notebook has been successfully created',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create notebook',
        variant: 'destructive',
      });
    },
  });
}

// Search Hook
export function useSearchIdeas() {
  const { user } = useAuth();

  return useMutation<Idea[], ApiError, string>({
    mutationFn: async (searchTerm) => {
      if (!user?.id) throw new Error('No user authenticated');

      const response = await fetchWithAuth(
        `${API_BASE_URL}/search?user_id=${user.id}&q=${encodeURIComponent(searchTerm)}`
      );
      return response.json();
    },
  });
}